<script setup lang="ts">
import {Check} from "@lucide/vue";
import {
    Stepper,
    StepperIndicator,
    StepperItem,
    StepperSeparator,
    StepperTitle,
    StepperTrigger
} from "@/core/components/ui/stepper";

/**
 * The steps of a renewal plan, and the way between them.
 *
 * The step being filled is active, the ones already gone through are checked
 * and stay open: going back to fix a step does not lock the following ones.
 * A step not reached yet, or one that follows an invalid step, stays out of reach.
 */

const props = defineProps<{
    furthestStep: number;
    canOpen: (step: number) => boolean;
}>();

const step = defineModel<number>("step", {required: true});

const labels = ['Saisir', 'Identifier', 'Configurer', 'Diffuser'];

/**
 * Whether a step has already been gone through, and can still be opened.
 *
 * A step locked by an invalid step before it loses its check, so that a checked
 * step is always one the pharmacist can go back to.
 */
const isCompleted = (position: number): boolean => {
    return position < props.furthestStep && position !== step.value && props.canOpen(position);
};
</script>

<template>
  <Stepper
    v-model="step"
    :linear="false"
    class="flex flex-1 items-center gap-4 rounded-full bg-white px-6 py-3 shadow-custom"
  >
    <StepperItem
      v-for="(label, index) in labels"
      :key="label"
      :step="index + 1"
      :completed="isCompleted(index + 1)"
      :disabled="!canOpen(index + 1)"
      class="flex items-center gap-4"
      :class="index !== labels.length - 1 && 'flex-1'"
    >
      <StepperTrigger
        class="group/trigger flex-row gap-3 p-0 rounded-full enabled:cursor-pointer focus-visible:outline-none"
      >
        <StepperIndicator
          class="size-7 shrink-0 bg-gray-400 text-white text-sm font-semibold
            transition-transform duration-200 group-hover/trigger:scale-110
            group-focus-visible/trigger:ring-2 group-focus-visible/trigger:ring-offset-2 group-focus-visible/trigger:ring-primary
            group-data-[disabled]:opacity-100 group-data-[disabled]:text-white
            group-data-[state=active]:bg-primary group-data-[state=active]:text-white
            group-data-[state=completed]:bg-secondary group-data-[state=completed]:text-primary-dark"
        >
          <Check v-if="isCompleted(index + 1)" class="size-4" :stroke-width="3" />
          <span v-else>{{ index + 1 }}</span>
        </StepperIndicator>

        <StepperTitle
          class="text-lg font-normal text-gray-dark transition-colors group-hover/trigger:text-primary
            group-data-[state=active]:font-semibold group-data-[state=active]:text-primary"
        >
          {{ label }}
        </StepperTitle>
      </StepperTrigger>

      <StepperSeparator
        v-if="index !== labels.length - 1"
        class="block h-0.5 flex-1 rounded-full bg-gray-200 group-data-[state=completed]:bg-secondary"
      />
    </StepperItem>
  </Stepper>
</template>
